import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useContent } from "@/lib/content";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

export default function AdminInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const c = useContent();
  const [b, setB] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("hw_token")) return navigate("/admin/login");
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => navigate("/admin"));
  }, [id, navigate]);

  if (!b) return <main className="min-h-screen bg-cream flex items-center justify-center text-ink/50" data-testid="invoice-loading">Rechnung wird geladen …</main>;

  const netto = Math.round((b.total_price / 1.07) * 100) / 100;
  const mwst = Math.round((b.total_price - netto) * 100) / 100;
  const invoiceNo = `RE-${new Date(b.created_at).getFullYear()}-${b.reference.replace("WP-", "")}`;
  const guestAddress = [b.guest_street, [b.guest_zip, b.guest_city].filter(Boolean).join(" "), b.guest_country].filter(Boolean).join(" · ");

  return (
    <main className="min-h-screen bg-cream py-10 print:bg-white print:py-0" data-testid="invoice-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/admin" data-testid="invoice-back-link" className="flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-forest transition-colors">
            <ArrowLeft size={16} /> Zurück zur Buchungsverwaltung
          </Link>
          <button onClick={() => window.print()} data-testid="invoice-print-button"
            className="flex items-center gap-2 rounded-full bg-forest text-sand px-6 py-2.5 text-sm font-semibold hover:bg-forest-dark transition-colors">
            <Printer size={15} /> Drucken / als PDF speichern
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-forest/10 p-10 sm:p-14 print:border-0 print:rounded-none print:p-0" data-testid="invoice-document">
          <div className="flex items-start justify-between">
            <img src="/logo.png" alt="Hotel Wildpferd" className="h-20 w-auto" />
            <div className="text-right text-xs text-ink/60 leading-relaxed">
              <p className="font-semibold text-ink text-sm">{c.invoice_company}</p>
              <p>Hotel Wildpferd Dülmen</p>
              <p>{c.contact_address_1} · {c.contact_address_2}</p>
              <p>Tel. {c.contact_phone} · {c.contact_email}</p>
              {c.invoice_tax_id ? <p>St-Nr./USt-IdNr.: {c.invoice_tax_id}</p> : <p className="text-terra">Steuernummer im Admin-Bereich hinterlegen (Website-Inhalte → Rechnungen)</p>}
            </div>
          </div>

          <div className="mt-12">
            <p className="text-sm leading-relaxed">
              {b.guest_name}<br />
              {b.guest_street && <>{b.guest_street}<br /></>}
              {(b.guest_zip || b.guest_city) && <>{[b.guest_zip, b.guest_city].filter(Boolean).join(" ")}<br /></>}
              {b.guest_email}
            </p>
            {!guestAddress && <p className="mt-1 text-xs text-ink/40">(keine Adresse hinterlegt)</p>}
          </div>

          <div className="mt-10 flex items-end justify-between">
            <h1 className="font-serif text-3xl font-semibold text-forest">Rechnung</h1>
            <div className="text-right text-sm text-ink/70 space-y-1">
              <p>Rechnungsnr.: <strong className="font-mono" data-testid="invoice-number">{invoiceNo}</strong></p>
              <p>Buchungsnr.: <span className="font-mono">{b.reference}</span></p>
              <p>Datum: {fmtDate(b.created_at)}</p>
              <p>Leistungszeitraum: {fmtDate(b.check_in)} – {fmtDate(b.check_out)}</p>
            </div>
          </div>

          <table className="mt-10 w-full text-sm">
            <thead>
              <tr className="border-b-2 border-forest text-left text-xs uppercase tracking-wider text-ink/50">
                <th className="py-3">Position</th>
                <th className="py-3 text-right">Nächte</th>
                <th className="py-3 text-right">Einzelpreis</th>
                <th className="py-3 text-right">Betrag</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-forest/10">
                <td className="py-4">Übernachtung — {b.rooms_count > 1 ? `${b.rooms_count} × ` : ""}{b.room_name}</td>
                <td className="py-4 text-right">{b.nights}</td>
                <td className="py-4 text-right">{eur(b.total_price / b.nights / (b.rooms_count || 1))}</td>
                <td className="py-4 text-right font-semibold">{eur(b.total_price)}</td>
              </tr>
            </tbody>
          </table>

          <div className="mt-6 flex justify-end">
            <dl className="w-64 text-sm space-y-1.5">
              <div className="flex justify-between"><dt className="text-ink/60">Netto</dt><dd>{eur(netto)}</dd></div>
              <div className="flex justify-between"><dt className="text-ink/60">inkl. 7 % MwSt.</dt><dd>{eur(mwst)}</dd></div>
              <div className="flex justify-between pt-2 border-t border-forest/20 text-base font-semibold text-forest">
                <dt>Gesamt</dt><dd data-testid="invoice-total">{eur(b.total_price)}</dd>
              </div>
            </dl>
          </div>

          <p className="mt-8 text-sm">
            Zahlungsstatus:{" "}
            <strong className={b.payment_status === "paid" ? "text-forest" : "text-terra"} data-testid="invoice-payment-status">
              {b.payment_status === "paid" ? "Bezahlt (Online-Zahlung)" : b.payment_method === "ueberweisung" ? "Offen — bitte per Überweisung zahlen" : "Offen"}
            </strong>
          </p>
          {b.payment_method === "ueberweisung" && b.payment_status !== "paid" && (
            <p className="mt-2 text-xs text-ink/60">
              {c.invoice_company} · IBAN {c.bank_iban} · BIC {c.bank_bic} · Verwendungszweck {b.reference}
            </p>
          )}

          <p className="mt-12 text-xs text-ink/50 leading-relaxed border-t border-forest/10 pt-6">{c.invoice_note}</p>
        </div>
      </div>
    </main>
  );
}
